import { cn } from "@/lib/utils";

/**
 * Risk budget bar — the worst-case-loss share of the cycle budget as a
 * coloured fill (green / amber / red zones at 70 / 90 %). Empty track when WCL
 * is null (no cycle / positions endpoint failed) — honest absence, never a
 * fake 0%. Extracted from RiskPanel (C3) so the cockpit reuses the SAME
 * visual instead of inventing a second budget gauge.
 */
export default function RiskBudgetBar({
  budget,
  worstCaseLoss,
}: {
  budget: number | null;
  worstCaseLoss: number | null;
}) {
  if (budget == null) {
    return (
      <div
        className="risk-bar bg-surface-2 h-2 w-full overflow-hidden rounded-full risk-bar--empty"
        aria-hidden
      />
    );
  }
  const pct =
    worstCaseLoss == null
      ? 0
      : Math.min(100, Math.round((worstCaseLoss / budget) * 100));
  const zone = pct >= 90 ? "red" : pct >= 70 ? "amber" : "green";
  const fillColor =
    zone === "red" ? "bg-sell" : zone === "amber" ? "bg-warning" : "bg-buy";
  return (
    <div
      className="risk-bar bg-surface-2 relative h-2 w-full overflow-hidden rounded-full"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label="Cycle risk budget used"
    >
      <div
        className={cn(
          "risk-bar__fill h-full rounded-full transition-[width]",
          `risk-bar__fill--${zone}`,
          fillColor,
        )}
        style={{ width: `${pct}%` }}
      />
      <span className="sr-only">
        {worstCaseLoss == null ? "—" : `${pct}% used`}
      </span>
    </div>
  );
}
